import {
  UCD_ACTION_TYPES,
  UCD_RESOURCE_FIELDS,
  UCD_RESOURCE_NAMES,
} from 'omega-rtc/constants/ucd';
import { hasPermission } from 'omega-rtc/utils/user-utils';

export type ContentConfigPermissions = {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canReview: boolean;
};

type ProductSubmitPermissions = Pick<ContentConfigPermissions, 'canCreate' | 'canUpdate'>;
type UpdatePermissions = Pick<ContentConfigPermissions, 'canUpdate'>;
type ReviewPermissions = Pick<ContentConfigPermissions, 'canReview'>;

type ProductSubmitState = {
  isDraft: boolean;
  draftReady: boolean;
  hasActive: boolean;
  hasEdits: boolean;
  hasPending: boolean;
  hasInvalidAudiences: boolean;
  hasInvalidAudienceReasons: boolean;
};

type CvpUpdateState = {
  hasActive: boolean;
  hasDescription: boolean;
  hasEdits: boolean;
  hasPending: boolean;
};

export type AudienceSubmitState = {
  isDraft: boolean;
  isValid: boolean;
  hasActive: boolean;
  hasEdits: boolean;
  hasPending: boolean;
};

export const EMPTY_CONTENT_CONFIG_PERMISSIONS: ContentConfigPermissions = {
  canView: false,
  canCreate: false,
  canUpdate: false,
  canReview: false,
};

export const getContentConfigPermissions = (userPermissions: any): ContentConfigPermissions => ({
  canView: hasPermission(
    userPermissions,
    UCD_RESOURCE_NAMES.project,
    UCD_RESOURCE_FIELDS.all,
    UCD_ACTION_TYPES.read,
  ),
  canCreate: hasPermission(
    userPermissions,
    UCD_RESOURCE_NAMES.contentConfig,
    '*',
    UCD_ACTION_TYPES.create,
  ),
  canUpdate: hasPermission(
    userPermissions,
    UCD_RESOURCE_NAMES.contentConfig,
    '*',
    UCD_ACTION_TYPES.update,
  ),
  canReview: hasPermission(
    userPermissions,
    UCD_RESOURCE_NAMES.contentConfig,
    '*',
    UCD_ACTION_TYPES.active,
  ),
});

export const canSubmitProductForApproval = (
  permissions: ProductSubmitPermissions,
  state: ProductSubmitState,
): boolean => {
  if (state.isDraft) {
    return permissions.canCreate
      && state.draftReady
      && !state.hasActive
      && !state.hasPending
      && !state.hasInvalidAudiences
      && !state.hasInvalidAudienceReasons;
  }

  return permissions.canUpdate
    && state.hasActive
    && state.hasEdits
    && !state.hasPending
    && !state.hasInvalidAudiences
    && !state.hasInvalidAudienceReasons;
};

export const canSubmitCvpUpdate = (
  permissions: UpdatePermissions,
  state: CvpUpdateState,
): boolean => permissions.canUpdate
  && state.hasActive
  && state.hasDescription
  && state.hasEdits
  && !state.hasPending;

export const canSubmitAudienceForApproval = (
  permissions: Pick<ContentConfigPermissions, 'canCreate' | 'canUpdate'>,
  state: AudienceSubmitState,
): boolean => {
  if (state.isDraft) {
    return permissions.canCreate
      && state.isValid
      && !state.hasActive
      && !state.hasPending;
  }

  return permissions.canUpdate
    && state.isValid
    && state.hasActive
    && state.hasEdits
    && !state.hasPending;
};

export const canRecallPendingChange = (
  permissions: UpdatePermissions,
  isMine: boolean,
): boolean => permissions.canUpdate && isMine;

export const canReviewPendingChange = (
  permissions: ReviewPermissions,
  isMine: boolean,
): boolean => permissions.canReview && !isMine;
